import { useEffect, useRef, useState } from 'react';
import { api } from '../api/client';

/**
 * Le direct en cours, s'il y en a un.
 *
 * Le plus simple des trois régimes de l'Activity, et de loin. Une projection
 * demande une horloge partagée, une séance spontanée demande un maître et un
 * relais ; un direct ne demande **rien**. Tout le monde regarde le bout du
 * flux, qui est le même pour tous. Il n'y a ni position à diffuser, ni
 * retardataire à rattraper, ni main à reprendre — arriver en cours de route,
 * c'est allumer la télévision.
 *
 * D'où l'absence ici de tout ce qui fait `useSeance` et `useProjection` : pas
 * de décalage d'horloge à mesurer, pas de position à recalculer. On demande
 * seulement, de temps en temps, si quelqu'un émet.
 */

const RELECTURE_MS = 10_000;

export function useDirect({ channelId }) {
  const [direct, setDirect] = useState(null);
  const [chargee, setChargee] = useState(false);

  // La relecture doit continuer même quand rien ne change, sans que l'effet ne
  // se reconstruise à chaque réponse : sinon on relancerait un minuteur à
  // chaque battement.
  const directRef = useRef(null);
  directRef.current = direct;

  useEffect(() => {
    let vivant = true;

    const relire = async () => {
      try {
        const parametres = channelId ? `?channel_id=${encodeURIComponent(channelId)}` : '';
        const donnees = await api.get(`/live/current${parametres}`);

        if (!vivant) return;

        const recu = donnees?.live ?? null;

        // Ne remplacer l'objet que lorsqu'il change vraiment : le rendre
        // systématiquement ferait repartir le lecteur toutes les dix secondes,
        // puisque `Direct` se recharge quand le manifeste change d'identité.
        //
        // Le titre fait partie de ce qui compte : il se corrige souvent en
        // cours de route — on lance d'abord, on nomme ensuite — et l'écran
        // afficherait sinon le nom de la diffusion précédente jusqu'au bout.
        setDirect((precedent) => {
          const identique = precedent?.id === recu?.id
            && precedent?.on_air === recu?.on_air
            && precedent?.waiting === recu?.waiting
            && precedent?.title === recu?.title;

          return identique ? precedent : recu;
        });
      } catch {
        // Un direct introuvable n'est pas une erreur : l'Activity retombe sur
        // son fonctionnement ordinaire, catalogue et projections.
      } finally {
        if (vivant) setChargee(true);
      }
    };

    relire();

    // Plus souvent que les projections : un direct commence sans prévenir,
    // alors qu'une séance programmée est attendue à l'heure dite.
    const minuteur = setInterval(relire, RELECTURE_MS);

    return () => { vivant = false; clearInterval(minuteur); };
  }, [channelId]);

  return { direct, chargee };
}
